import React from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import routes from 'lane-shared/config/routes';

import { ErrorMessage, Loading } from 'components';
import BreadCrumbs from 'components/lds/BreadCrumbs';

import { getServiceRequestByIdQuery } from 'graphql-queries';

import styles from './ServiceRequestEditForm.scss';
import { ServiceRequestEditForm } from './ServiceRequestEditForm';
import { ActiveChannelTypeEnum } from 'constants-channel';
import type { AddressType } from 'application-types';

interface ChannelProp {
  slug: string;
  type: ActiveChannelTypeEnum;
  address?: Pick<AddressType, 'geo'>;
}

interface ServiceRequestEditPageProps {
  channel: ChannelProp | undefined;
}

export function ServiceRequestEditPage({
  channel,
}: ServiceRequestEditPageProps) {
  const { t } = useTranslation();
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();

  const {
    data: serviceRequestResponse,
    refetch,
    loading: srLoading,
    error,
  } = useQuery(getServiceRequestByIdQuery, {
    variables: { serviceRequestId },
  });

  const serviceRequestData = serviceRequestResponse?.serviceRequest;
  const serviceRequestListPath = channel?.slug
    ? routes.channelAdminWorkOrdersServiceRequests.replace(':id', channel.slug)
    : '';

  // Show loading state
  if (srLoading) {
    return (
      <div className={styles.container}>
        <Loading />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={styles.container}>
        <ErrorMessage error={error} />
      </div>
    );
  }

  // Guard: ensure data exists before rendering form
  if (!serviceRequestData?.serviceRequestId || !channel?.slug) {
    return null;
  }

  return (
    <div className={styles.container}>
      <BreadCrumbs
        links={[
          {
            label: t('web.admin.serviceRequest.title'),
            url: serviceRequestListPath,
          },
          {
            label: `${serviceRequestData?.userFriendlyID}`,
            url: routes.channelAdminWorkOrdersServiceRequestDetails
              .replace(':id', channel?.slug)
              .replace(
                ':serviceRequestId',
                serviceRequestData?.serviceRequestId
              ),
          },
          {
            label: t('web.admin.serviceRequest.edit'),
          },
        ]}
      />
      <ServiceRequestEditForm
        channel={channel}
        serviceRequest={serviceRequestData}
        getServiceRequest={refetch}
      />
    </div>
  );
}
